import './as-option.ts';
import './as-help-hover.ts';
import { updateShadowDOMHelpHover } from './input-help-hover-utils.ts';
import styleText from './as-dropdown.css?raw';
import type { SetValueOptions } from './types';
import { resolveSetValueSilence, upgradeProperty } from './utils';
import { AsInputBase, type InternalSetValueOptions } from './as-input-base.ts';
import { computePosition, flip, shift, offset, autoUpdate, size } from '@floating-ui/dom';

type OptionElement = HTMLElement & {
  value?: string;
  label?: string;
  disabled?: boolean;
};

type SelectionUpdateOptions = {
  emitInput?: boolean;
  emitChange?: boolean;
  reflect?: boolean;
  focus?: boolean;
};

const template = document.createElement('template');
template.innerHTML = /*html*/`
  <div class="as-dropdown-label-container">
    <label class="as-dropdown-label"></label>
    <span class="help-hover-container"></span>
  </div>
  <div class="as-dropdown-container">
    <button
      type="button"
      class="as-dropdown-selected"
      aria-haspopup="listbox"
      aria-expanded="false"
    >
      <span class="as-dropdown-selected-text"></span>
      <span class="as-dropdown-arrow" aria-hidden="true"></span>
    </button>
    <slot style="display:none;"></slot>
  </div>
`;

// Styles for the portaled list
const portalListStyles = `
  .as-dropdown-portal-list {
    position: fixed;
    z-index: 9999;
    background: var(--numbox-container-bg, #ffffff);
    border: 1px solid var(--color-tr-blue-desat, #5397a8);
    border-radius: var(--radius, 0.5rem);
    box-shadow: var(--shadow-medium, 0 4px 6px -1px hsl(196 100% 11% / 0.1), 0 2px 4px -1px hsl(196 100% 11% / 0.06));
    padding: 0.25em;
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    font-family: var(--font-family-body, sans-serif);
  }
  .as-dropdown-portal-list[hidden] {
    display: none;
  }
  .as-dropdown-portal-list .as-dropdown-option {
    display: block;
    margin: 0.125em 0;
    padding: 0.35em 0.75em;
    border: none;
    background: none;
    outline: none;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
    border-radius: var(--radius, 0.5rem);
    text-align: left;
    width: 100%;
    box-sizing: border-box;
  }
  .as-dropdown-portal-list .as-dropdown-option:hover,
  .as-dropdown-portal-list .as-dropdown-option[data-highlighted] {
    background-color: var(--color-tr-blue-light-desat, #e0f4fa);
    color: var(--color-tr-blue, #0084a4);
  }
  .as-dropdown-portal-list .as-dropdown-option[data-selected] {
    font-weight: 600;
  }
  .as-dropdown-portal-list .as-dropdown-option[disabled] {
    color: #aaa;
    cursor: not-allowed;
  }
  .as-dropdown-portal-list .as-dropdown-option[disabled]:hover {
    background: none;
    color: #aaa;
  }
`;

/**
 * as-dropdown: Custom dropdown that works with light-DOM as-option children.
 * Uses a portal pattern for the options list to avoid scroll container clipping.
 */
export class AsDropdown extends AsInputBase<"int"|"string"> {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  // Inject portal styles once
  private static _portalStylesInjected = false;
  private static _injectPortalStyles() {
    if (AsDropdown._portalStylesInjected) return;
    const style = document.createElement('style');
    style.textContent = portalListStyles;
    document.head.appendChild(style);
    AsDropdown._portalStylesInjected = true;
  }

  static get observedAttributes() {
    return ['label-default-text', 'label-dict-key', 'disabled', 'hide', 'value', 'help-tooltip-default-text', 'help-tooltip-dict-key', 'help-placement'];
  }

  // Shadow DOM refs
  private _shadowRoot?: ShadowRoot;
  private _labelEl?: HTMLLabelElement;
  private _helpHoverEl?: HTMLElement;
  private _triggerEl?: HTMLButtonElement;
  private _selectedTextEl?: HTMLElement;
  private _slotEl?: HTMLSlotElement;

  // Portal
  private _portalList?: HTMLElement;
  private _portalCleanup?: () => void;

  // State
  private _open = false;
  private _options: OptionElement[] = [];
  private _selectedIndex = -1;
  private _labelText = '';
  private _hoveredIndex: number | null = null;
  private _isSettingValueAttr = false;
  private _isSyncingOptions = false;

  constructor() {
    super();
    this._value = null as any;
  }

  private _optionObserver = new MutationObserver(() => {
    if (this._isSyncingOptions) return;
    this._syncSelectionFromOptions();
    if (this._open) this._syncPortaledOptions();
  });

  // ============================================================================
  // AsInputBase abstract method implementations
  // ============================================================================

  protected _getValue(): string | number {
    if (this.hasAttribute('send-index')) {
      return this._selectedIndex;
    }

    const option = this._selectedIndex >= 0 ? this._options[this._selectedIndex] : null;
    if (option) {
      const valueAttr = option.getAttribute('value');
      if (valueAttr !== null) {
        if (this.getAttribute('value-type') === 'int') {
          const num = parseInt(valueAttr, 10);
          return Number.isNaN(num) ? valueAttr : num;
        }
        return valueAttr;
      }
    }

    return this._selectedIndex;
  }

  protected _applyValue(val: string | number, _options: InternalSetValueOptions): void {
    if (val === null || val === undefined) {
      this._clearSelection({ emitInput: false, emitChange: false, reflect: true });
      return;
    }

    // 1. If explicitly sending indices, val must be interpreted as an index
    if (this.hasAttribute('send-index')) {
      const idx = typeof val === 'number' ? val : parseInt(val as string, 10);
      this._setSelectedIndex(idx, { emitInput: false, emitChange: false, reflect: true });
      return;
    }

    // 2. Try to find by explicit value attribute
    const isValueTypeInt = this.getAttribute('value-type') === 'int';
    const stringVal = String(val);

    const valIdx = this._options.findIndex(opt => {
      const optVal = opt.getAttribute('value');
      if (optVal === null) return false;
      return optVal === stringVal;
    });

    if (valIdx !== -1) {
      this._setSelectedIndex(valIdx, { emitInput: false, emitChange: false, reflect: true });
      return;
    }

    // 3. Fallback: If val is numeric, it might be an index
    if (typeof val === 'number') {
      this._setSelectedIndex(val, { emitInput: false, emitChange: false, reflect: true });
      return;
    }

    // 4. Last resort: try derived value (label, text)
    const derivedIdx = this._options.findIndex(opt => this._deriveOptionValue(opt) === stringVal);
    if (derivedIdx !== -1) {
      this._setSelectedIndex(derivedIdx, { emitInput: false, emitChange: false, reflect: true });
    }
  }

  protected _convertFromParamate(val: any): string | number {
    if (typeof val === 'number' || typeof val === 'string') {
      return val;
    }
    const parsed = parseInt(val, 10);
    return Number.isNaN(parsed) ? -1 : parsed;
  }

  public override resetToDefault(): void {
    this._value = this._initialValueAttr;
    this._syncSelectionFromAttributes();
  }

  protected _getInputElement(): HTMLButtonElement | null {
    return this._triggerEl ?? null;
  }

  protected override _applyReadonlyState(): void {
    if (this.readonly && this._open) {
      this._closeDropdown(false);
    }
    if (this._triggerEl) {
      this._triggerEl.style.cursor = this.readonly ? 'default' : '';
    }
  }

  connectedCallback() {
    AsDropdown._injectPortalStyles();
    upgradeProperty(this, 'value');
    upgradeProperty(this, 'disabled');
    upgradeProperty(this, 'hide');
    this._initializeOnce();
    this._collectOptions();
    this._syncSelectionFromAttributes();
    this._updateComponentState();
    this._updateHelpHover();
    document.addEventListener('mousedown', this._handleDocumentMouseDown, true);

    this._registerParamate();
  }

  disconnectedCallback() {
    document.removeEventListener('mousedown', this._handleDocumentMouseDown, true);
    this._unregisterParamate();
    this._optionObserver.disconnect();
    this._removePortaledList();
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null) {
    if (name === 'label-default-text') {
      this._labelText = newValue ?? '';
      this._updateLabelText();
    } else if (name === 'disabled') {
      if (this.disabled && this._open) {
        this._closeDropdown(false);
      }
      this._updateTriggerState();
    } else if (name === 'hide') {
      // handled via :host([hide]) selector
    } else if (name === 'value') {
      if (this._isSettingValueAttr) {
        return;
      }
      if (newValue === null) {
        this._clearSelection({ emitInput: false, emitChange: true, reflect: false });
      } else {
        this._selectByValue(newValue, { emitInput: false, emitChange: true, reflect: false });
      }
    } else if (name === 'help-tooltip-default-text' || name === 'help-tooltip-dict-key' || name === 'help-placement') {
      this._updateHelpHover();
    }
  }

  get value() {
    return this._value;
  }

  set value(val: string | number | null) {
    this.setValue(val);
  }

  get selectedIndex(): number {
    return this._selectedIndex;
  }

  public setValue(val: string | number | null, opt?: SetValueOptions): void {
    const { silenceInput, silenceChange, silenceHistory } = resolveSetValueSilence(opt);
    if (silenceHistory) this._skipNextHistoryPush = true;
    const emitInput = !silenceInput;
    const emitChange = !silenceChange;

    if (val === null) {
      this._clearSelection({ emitInput, emitChange, reflect: true });
    } else if (typeof val === 'number') {
      if (val < 0) {
        this._clearSelection({ emitInput, emitChange, reflect: true });
      } else {
        this._setSelectedIndex(val, { emitInput, emitChange, reflect: true });
      }
    } else {
      this._selectByValue(val, { emitInput, emitChange, reflect: true });
    }

    this._skipNextHistoryPush = false;
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  private _initializeOnce() {
    if (!this._shadowRoot) {
      this._shadowRoot = this.attachShadow({ mode: 'open' });
      this._shadowRoot.appendChild(template.content.cloneNode(true));
      this._shadowRoot.adoptedStyleSheets = [
        ...this._shadowRoot.adoptedStyleSheets,
        AsDropdown.sheet,
      ];

      this._labelEl = this._shadowRoot.querySelector('.as-dropdown-label') as HTMLLabelElement;
      this._helpHoverEl = this._shadowRoot.querySelector('.help-hover-container') as HTMLElement;
      this._triggerEl = this._shadowRoot.querySelector('.as-dropdown-selected') as HTMLButtonElement;
      this._selectedTextEl = this._shadowRoot.querySelector('.as-dropdown-selected-text') as HTMLElement;
      this._slotEl = this._shadowRoot.querySelector('slot') as HTMLSlotElement;

      this.setAttribute('role', 'combobox');
      this.setAttribute('aria-haspopup', 'listbox');

      this._triggerEl?.addEventListener('click', this._toggleDropdown);
      this._triggerEl?.addEventListener('keydown', this._onTriggerKeyDown);
      this._slotEl?.addEventListener('slotchange', this._onSlotChange);
    }
  }

  // ============================================================================
  // Portal Management
  // ============================================================================

  private _createPortaledList() {
    if (this._portalList) return;

    const list = document.createElement('div');
    list.className = 'as-dropdown-portal-list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    list.addEventListener('click', this._onPortalListClick);
    list.addEventListener('pointerover', this._onPortalListPointerOver);
    list.addEventListener('pointerout', this._onPortalListPointerOut);

    this._findPortalContainer().appendChild(list);
    this._portalList = list;
  }

  private _removePortaledList() {
    if (this._portalCleanup) {
      this._portalCleanup();
      this._portalCleanup = undefined;
    }
    if (this._portalList) {
      this._portalList.removeEventListener('click', this._onPortalListClick);
      this._portalList.removeEventListener('pointerover', this._onPortalListPointerOver);
      this._portalList.removeEventListener('pointerout', this._onPortalListPointerOut);
      this._portalList.remove();
      this._portalList = undefined;
    }
  }

  /**
   * Walk up the ancestor chain (crossing shadow DOM boundaries) to find an
   * element whose shadow root contains an open <dialog>. When found, the
   * portal list is appended inside that dialog so it sits within the
   * interactive subtree — avoiding the pointer-event blockage that
   * showModal() imposes on everything outside the dialog. Also injects portal
   * styles into that shadow root so class-based selectors resolve correctly.
   * Falls back to document.body when no modal ancestor is detected.
   */
  private _findPortalContainer(): Element {
    let node: Node | null = this.parentNode;
    while (node) {
      if (node instanceof ShadowRoot) {
        node = node.host;
      } else if (node instanceof Element) {
        if (node.shadowRoot) {
          const dialog = node.shadowRoot.querySelector('dialog[open]');
          if (dialog) {
            this._ensurePortalStylesInRoot(node.shadowRoot);
            return dialog;
          }
        }
        node = node.parentNode;
      } else {
        break;
      }
    }
    return document.body;
  }

  private _ensurePortalStylesInRoot(root: ShadowRoot): void {
    if (root.querySelector('style[data-as-dropdown-portal]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-as-dropdown-portal', '');
    style.textContent = portalListStyles;
    root.appendChild(style);
  }

  private _syncPortaledOptions() {
    if (!this._portalList) return;

    this._portalList.innerHTML = '';
    this._options.forEach((opt, index) => {
      const clone = document.createElement('div');
      clone.className = 'as-dropdown-option';
      clone.textContent = opt.getAttribute('label') ?? opt.textContent?.trim() ?? '';
      clone.dataset.optionIndex = String(index);

      if (opt.hasAttribute('disabled')) {
        clone.setAttribute('disabled', '');
      }
      if (index === this._selectedIndex) {
        clone.setAttribute('data-selected', '');
      }
      if (index === this._hoveredIndex) {
        clone.setAttribute('data-highlighted', '');
      }

      this._portalList!.appendChild(clone);
    });
  }

  private _updatePortalHighlightState() {
    if (!this._portalList) return;

    const options = this._portalList.querySelectorAll('.as-dropdown-option');
    options.forEach((opt, index) => {
      const shouldHighlight = index === this._hoveredIndex;
      opt.toggleAttribute('data-highlighted', shouldHighlight);
    });
  }

  private _updateListPosition() {
    if (!this._portalList || !this._triggerEl) return;

    // Clean up previous autoUpdate
    if (this._portalCleanup) {
      this._portalCleanup();
    }

    this._portalCleanup = autoUpdate(this._triggerEl, this._portalList, async () => {
      if (!this._portalList || !this._triggerEl) return;

      const { x, y } = await computePosition(this._triggerEl, this._portalList, {
        placement: 'bottom-start',
        strategy: 'fixed',
        middleware: [
          offset(4),
          flip({ fallbackPlacements: ['top-start'] }),
          shift({ padding: 8 }),
          size({
            apply({ availableHeight, elements }) {
              Object.assign(elements.floating.style, {
                maxHeight: `${Math.min(availableHeight - 16, 300)}px`,
              });
            },
          }),
        ],
      });

      Object.assign(this._portalList.style, {
        left: `${x}px`,
        top: `${y}px`,
        minWidth: `${this._triggerEl.offsetWidth}px`,
      });
    });
  }

  private _onPortalListClick = (event: Event) => {
    if (this.disabled || this.readonly) return;

    const target = (event.target as HTMLElement).closest('.as-dropdown-option') as HTMLElement | null;
    if (!target) return;

    const indexStr = target.dataset.optionIndex;
    if (indexStr == null) return;

    const index = parseInt(indexStr, 10);
    if (Number.isNaN(index) || target.hasAttribute('disabled')) return;

    this._setSelectedIndex(index, { emitInput: true, emitChange: true, reflect: true });
    this._closeDropdown(false);
  };

  private _onPortalListPointerOver = (event: Event) => {
    const target = (event.target as HTMLElement).closest('.as-dropdown-option') as HTMLElement | null;
    if (!target) return;

    const indexStr = target.dataset.optionIndex;
    if (indexStr == null) return;

    const index = parseInt(indexStr, 10);
    if (!Number.isNaN(index) && !target.hasAttribute('disabled')) {
      this._hoveredIndex = index;
      this._updatePortalHighlightState();
    }
  };

  private _onPortalListPointerOut = (event: Event) => {
    const target = (event.target as HTMLElement).closest('.as-dropdown-option') as HTMLElement | null;
    if (!target) return;

    const indexStr = target.dataset.optionIndex;
    if (indexStr == null) return;

    const index = parseInt(indexStr, 10);
    if (!Number.isNaN(index) && this._hoveredIndex === index) {
      this._hoveredIndex = null;
      this._updatePortalHighlightState();
    }
  };

  // ============================================================================
  // Options Management
  // ============================================================================

  private _collectOptions() {
    const assigned = this._slotEl
      ? this._slotEl.assignedElements({ flatten: true })
      : Array.from(this.querySelectorAll('as-option'));

    const newOptions = assigned.filter(
      (node): node is OptionElement => node instanceof HTMLElement && node.tagName.toLowerCase() === 'as-option'
    );

    this._optionObserver.disconnect();

    this._options = newOptions;
    this._hoveredIndex = null;
    this._options.forEach((opt, index) => {
      opt.dataset.optionIndex = String(index);
      this._optionObserver.observe(opt, {
        attributes: true,
        attributeFilter: ['selected', 'disabled', 'label', 'value'],
      });
    });
  }

  private _syncSelectionFromAttributes() {
    if (!this._options.length) {
      this._clearSelection({ emitChange: false, reflect: true });
      return;
    }

    if (this._value !== null && this._selectByValue(this._value, { emitChange: false })) {
      return;
    }

    const selectedIdx = this._options.findIndex(opt => opt.hasAttribute('selected'));
    if (selectedIdx !== -1) {
      this._setSelectedIndex(selectedIdx, { emitInput: false, emitChange: false, reflect: true });
      return;
    }

    this._selectFirstAvailable({ emitChange: false });
  }

  private _syncSelectionFromOptions() {
    if (!this._options.length) {
      this._clearSelection({ emitChange: false, reflect: true });
      return;
    }

    const selectedIdx = this._options.findIndex(opt => opt.hasAttribute('selected'));
    if (selectedIdx !== -1 && selectedIdx !== this._selectedIndex) {
      this._setSelectedIndex(selectedIdx, { emitInput: false, emitChange: false, reflect: true });
      return;
    }

    if (this._value !== null) {
      this._selectByValue(this._value, { emitChange: false });
      return;
    }

    if (this._selectedIndex === -1) {
      this._selectFirstAvailable({ emitChange: false });
    } else {
      this._updateTriggerState();
    }
  }

  private _selectFirstAvailable(options: SelectionUpdateOptions = {}) {
    const emitChange = options.emitChange ?? true;
    const emitInput = options.emitInput ?? emitChange;
    const idx = this._options.findIndex(opt => !opt.hasAttribute('disabled'));
    if (idx !== -1) {
      this._setSelectedIndex(idx, { emitInput, emitChange, reflect: true });
    } else {
      this._clearSelection({ emitInput, emitChange, reflect: true });
    }
  }

  private _selectByValue(
    val: string | number | null,
    options: SelectionUpdateOptions = {}
  ): boolean {
    const emitChange = options.emitChange ?? true;
    const emitInput = options.emitInput ?? emitChange;
    const reflect = options.reflect ?? true;

    if (val == null) {
      this._clearSelection({ emitInput, emitChange, reflect });
      return true;
    }

    const idx = this._options.findIndex(opt => this._deriveOptionValue(opt) === val);
    if (idx === -1) {
      return false;
    }

    this._setSelectedIndex(idx, { emitInput, emitChange, reflect });
    return true;
  }

  private _setSelectedIndex(
    index: number,
    options: SelectionUpdateOptions = {}
  ): void {
    const emitChange = options.emitChange ?? true;
    const emitInput = options.emitInput ?? emitChange;
    const reflect = options.reflect ?? true;

    if (index < 0 || index >= this._options.length) {
      this._clearSelection({ emitInput, emitChange, reflect });
      return;
    }

    const option = this._options[index];
    if (option.hasAttribute('disabled')) {
      return;
    }

    const previousIndex = this._selectedIndex;
    const previousOption = previousIndex >= 0 ? this._options[previousIndex] : null;
    const previousValue = this._value;

    this._isSyncingOptions = true;
    this._options.forEach((opt, i) => {
      const shouldSelect = i === index;
      if (opt.hasAttribute('selected') !== shouldSelect) {
        if (shouldSelect) {
          opt.setAttribute('selected', '');
        } else {
          opt.removeAttribute('selected');
        }
      }
      opt.setAttribute('aria-selected', shouldSelect ? 'true' : 'false');
    });
    this._isSyncingOptions = false;

    this._selectedIndex = index;
    const newValue = this._deriveOptionValue(option);
    this._updateValueInternal(newValue, {
      emitInput: emitInput && newValue !== previousValue,
      emitChange: emitChange && newValue !== previousValue,
      reflect,
      option,
      previousOption,
    });
    this._updateTriggerState();

    // Update portal if open
    if (this._open && this._portalList) {
      this._syncPortaledOptions();
    }
  }

  private _updateValueInternal(
    value: string | null,
    options: {
      emitInput: boolean;
      emitChange: boolean;
      reflect: boolean;
      option?: OptionElement | null;
      previousOption?: OptionElement | null;
    }
  ): void {
    const previous = this._value;
    this._value = value;

    if (options.reflect) {
      this._isSettingValueAttr = true;
      if (value === null) {
        this.removeAttribute('value');
      } else {
        this.setAttribute('value', value);
      }
      this._isSettingValueAttr = false;
    }

    const option = options.option ?? (this._selectedIndex >= 0 ? this._options[this._selectedIndex] : null);
    const label = this._getOptionLabel(option);
    const previousLabel = this._getOptionLabel(options.previousOption);

    if (options.emitInput && previous !== value) {
      this._emitInput(value, previous, label, previousLabel);
    }

    if (options.emitChange && previous !== value) {
      this._emitChange(value, label);
    }
  }

  private _emitInput(
    value: string | number | null,
    previousValue: string | number | null,
    label: string | null,
    previousLabel: string | null
  ): void {
    const detail = {
      type: 'dropdown' as const,
      value,
      previousValue,
      label,
      previousLabel,
      selectedIndex: this._selectedIndex,
      phase: 'input' as const,
    };

    this.dispatchEvent(
      new CustomEvent('as-input-update', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private _emitChange(value: string | null, label: string | null): void {
    const detail = {
      type: 'dropdown' as const,
      value,
      label,
      selectedIndex: this._selectedIndex,
      phase: 'change' as const,
    };
    this.dispatchEvent(
      new CustomEvent('as-input-change', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
    this.dispatchEvent(new CustomEvent('change', { detail: { value } }));

    this._sendToParamate(this._getValue());
  }

  private _getOptionLabel(option?: OptionElement | null): string | null {
    if (!option) {
      return null;
    }
    return option.getAttribute('label') ?? option.textContent?.trim() ?? null;
  }

  private _clearSelection(options: SelectionUpdateOptions = {}) {
    const emitChange = options.emitChange ?? true;
    const emitInput = options.emitInput ?? emitChange;
    const reflect = options.reflect ?? true;

    const previousValue = this._value;
    const previousIndex = this._selectedIndex;
    const previousOption = previousIndex >= 0 ? this._options[previousIndex] : null;

    this._isSyncingOptions = true;
    this._options.forEach(opt => {
      opt.removeAttribute('selected');
      opt.setAttribute('aria-selected', 'false');
    });
    this._isSyncingOptions = false;

    this._selectedIndex = -1;

    this._updateValueInternal(null, {
      emitInput: emitInput && previousValue !== null,
      emitChange: emitChange && previousValue !== null,
      reflect,
      option: null,
      previousOption,
    });

    this._updateTriggerState();

    if (this._open && this._portalList) {
      this._syncPortaledOptions();
    }
  }

  private _updateComponentState() {
    this._updateLabelText();
    this._updateTriggerState();
  }

  private _updateLabelText() {
    if (this._labelEl) {
      this._labelEl.textContent = this._labelText || this.getAttribute('label-default-text') || 'Option';
    }
  }

  private _updateTriggerState() {
    const option = this._options[this._selectedIndex];
    if (this._selectedTextEl) {
      const text =
        option?.getAttribute('label') ??
        option?.textContent?.trim() ??
        '';
      this._selectedTextEl.textContent = text;
    }

    if (this._triggerEl) {
      this._triggerEl.setAttribute('aria-expanded', this._open ? 'true' : 'false');
      this._triggerEl.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
      if (this.disabled) {
        this._triggerEl.setAttribute('disabled', '');
      } else {
        this._triggerEl.removeAttribute('disabled');
      }
    }

    this.toggleAttribute('data-open', this._open);
  }

  private _deriveOptionValue(option: OptionElement, indexOverride?: number) {
    const valueAttr = option.getAttribute('value');
    if (valueAttr != null && valueAttr.trim() !== '') {
      return valueAttr.trim();
    }
    const labelAttr = option.getAttribute('label');
    if (labelAttr && labelAttr.trim() !== '') {
      return labelAttr.trim();
    }
    const text = option.textContent?.trim();
    if (text && text !== '') {
      return text;
    }
    const idx = indexOverride ?? this._options.indexOf(option);
    return idx >= 0 ? String(idx) : '';
  }

  // ============================================================================
  // Open/Close
  // ============================================================================

  private _openDropdown() {
    if (this.disabled || this.readonly || this._open) {
      return;
    }
    this._open = true;
    this._hoveredIndex = this._selectedIndex;

    this._createPortaledList();
    this._syncPortaledOptions();

    if (this._portalList) {
      this._portalList.hidden = false;
      this._updateListPosition();
    }

    this._updateTriggerState();
  }

  private _closeDropdown(returnFocus = true) {
    if (!this._open) {
      return;
    }
    this._open = false;
    this._hoveredIndex = null;

    if (this._portalCleanup) {
      this._portalCleanup();
      this._portalCleanup = undefined;
    }

    if (this._portalList) {
      this._portalList.hidden = true;
    }

    this._updateTriggerState();

    if (returnFocus && this._triggerEl) {
      this._triggerEl.focus();
    }
  }

  private _toggleDropdown = () => {
    if (this.disabled || this.readonly) {
      return;
    }
    if (this._open) {
      this._closeDropdown();
    } else {
      this._openDropdown();
    }
  };

  private _onTriggerKeyDown = (event: KeyboardEvent) => {
    if (this.disabled || this.readonly) {
      return;
    }
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this._open && this._hoveredIndex != null && this._hoveredIndex >= 0) {
          this._setSelectedIndex(this._hoveredIndex, { emitInput: true, emitChange: true, reflect: true });
          this._closeDropdown();
        } else {
          this._toggleDropdown();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this._open) {
          this._openDropdown();
        } else {
          this._moveHover(1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this._open) {
          this._openDropdown();
        } else {
          this._moveHover(-1);
        }
        break;
      case 'Escape':
        if (this._open) {
          event.preventDefault();
          this._closeDropdown();
        }
        break;
      default:
        break;
    }
  };

  private _moveHover(delta: number) {
    if (!this._options.length) return;

    let idx = this._hoveredIndex ?? this._selectedIndex;
    const start = idx;
    const length = this._options.length;

    if (idx === -1) {
      idx = delta > 0 ? -1 : 0;
    }

    do {
      idx = (idx + delta + length) % length;
      const opt = this._options[idx];
      if (!opt.hasAttribute('disabled')) {
        this._hoveredIndex = idx;
        this._updatePortalHighlightState();
        this._scrollHoveredIntoView();
        return;
      }
    } while (idx !== start);
  }

  private _scrollHoveredIntoView() {
    if (this._hoveredIndex == null || !this._portalList) return;
    const el = this._portalList.children[this._hoveredIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }

  private _handleDocumentMouseDown = (event: MouseEvent) => {
    if (!this._open) return;
    const path = event.composedPath();
    if (path.includes(this)) return;
    if (this._portalList && path.includes(this._portalList)) return;
    this._closeDropdown(false);
  };

  private _onSlotChange = () => {
    this._collectOptions();
    this._syncSelectionFromOptions();
    this._updateComponentState();
    if (this._open) {
      this._syncPortaledOptions();
    }
  };

  private _updateHelpHover(): void {
    updateShadowDOMHelpHover(this, this._helpHoverEl);
  }
}

customElements.define('as-dropdown', AsDropdown);
